/**
 * Shared queue for JS-driven media/API work (not <video> byte streams).
 * Caps concurrency, de-dupes by key, supports AbortSignal, retries 429/503.
 */

const MAX_CONCURRENT = 3;
const MAX_RETRIES = 4;
const MAX_BACKOFF_MS = 15000;

const PRIORITY_RANK = {
  active: 0,
  next: 1,
  thumbnail: 2,
  low: 3,
};

/** @type {Map<string, Promise<unknown>>} */
const inflight = new Map();

/** @type {Array<{
 *  key: string,
 *  priority: number,
 *  seq: number,
 *  signal?: AbortSignal,
 *  task: (ctx: { signal?: AbortSignal, attempt: number }) => Promise<unknown>,
 *  resolve: (value: unknown) => void,
 *  reject: (reason?: unknown) => void,
 * }>} */
const queue = [];

let activeCount = 0;
let seqCounter = 0;

function rank(priority) {
  return PRIORITY_RANK[priority] ?? PRIORITY_RANK.low;
}

function createAbortError() {
  if (typeof DOMException === "function") {
    return new DOMException("Aborted", "AbortError");
  }
  const err = new Error("Aborted");
  err.name = "AbortError";
  return err;
}

export function isAbortError(error) {
  return error?.name === "AbortError" || error?.code === "ERR_CANCELED";
}

export function isRetryableStatus(status) {
  return status === 429 || status === 503;
}

export function getRetryAfterMs(headers, attempt) {
  const raw =
    headers?.["retry-after"] ??
    headers?.["Retry-After"] ??
    (typeof headers?.get === "function"
      ? headers.get("retry-after") || headers.get("Retry-After")
      : null);
  if (raw != null && raw !== "") {
    const asNumber = Number(raw);
    if (Number.isFinite(asNumber) && asNumber >= 0) {
      return Math.min(asNumber * 1000, MAX_BACKOFF_MS);
    }
    const asDate = Date.parse(String(raw));
    if (Number.isFinite(asDate)) {
      return Math.min(Math.max(0, asDate - Date.now()), MAX_BACKOFF_MS);
    }
  }
  const base = Math.min(1000 * 2 ** Math.max(0, attempt), MAX_BACKOFF_MS);
  const jitter = Math.floor(Math.random() * 250);
  return Math.min(base + jitter, MAX_BACKOFF_MS);
}

function statusFromError(error) {
  return error?.response?.status ?? error?.status;
}

function headersFromError(error) {
  return error?.response?.headers ?? error?.headers ?? null;
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(createAbortError());
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Run an async fn with 429/503 exponential backoff (+ Retry-After, jitter).
 * Does not retry other 4xx.
 */
export async function withMediaRetry(
  task,
  { signal, maxRetries = MAX_RETRIES } = {},
) {
  let attempt = 0;
  for (;;) {
    if (signal?.aborted) throw createAbortError();
    try {
      return await task({ signal, attempt });
    } catch (error) {
      if (signal?.aborted || isAbortError(error)) throw error;
      const status = statusFromError(error);
      if (!isRetryableStatus(status) || attempt >= maxRetries) throw error;
      const waitMs = getRetryAfterMs(headersFromError(error), attempt);
      attempt += 1;
      await sleep(waitMs, signal);
    }
  }
}

function finishJob(job, result, error) {
  activeCount -= 1;
  if (error) job.reject(error);
  else job.resolve(result);
  pump();
}

function startJob(job) {
  activeCount += 1;
  withMediaRetry(job.task, { signal: job.signal }).then(
    (value) => finishJob(job, value),
    (error) => finishJob(job, undefined, error),
  );
}

function pump() {
  while (activeCount < MAX_CONCURRENT && queue.length) {
    queue.sort((a, b) => a.priority - b.priority || a.seq - b.seq);
    const job = queue.shift();
    if (!job) break;
    if (job.signal?.aborted) {
      job.reject(createAbortError());
      continue;
    }
    startJob(job);
  }
}

/**
 * Enqueue a JS media/API task. Same `key` shares one in-flight promise.
 * @param {{
 *  key: string,
 *  priority?: 'active'|'next'|'thumbnail'|'low',
 *  signal?: AbortSignal,
 *  task: (ctx: { signal?: AbortSignal, attempt: number }) => Promise<unknown>,
 * }} options
 */
export function enqueueMediaTask({ key, priority = "low", signal, task }) {
  if (!key || typeof task !== "function") {
    return Promise.reject(new Error("enqueueMediaTask requires key and task"));
  }
  if (signal?.aborted) return Promise.reject(createAbortError());

  const existing = inflight.get(key);
  if (existing) return existing;

  let settle;
  const promise = new Promise((resolve, reject) => {
    settle = { resolve, reject };
  });

  inflight.set(key, promise);
  promise.finally(() => {
    if (inflight.get(key) === promise) inflight.delete(key);
  });

  queue.push({
    key,
    priority: rank(priority),
    seq: (seqCounter += 1),
    signal,
    task,
    resolve: settle.resolve,
    reject: settle.reject,
  });
  pump();

  return promise;
}

export function mediaLoaderStats() {
  return {
    active: activeCount,
    queued: queue.length,
    inflight: inflight.size,
  };
}
